import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Device } from 'mediasoup-client';
import { Consumer, Transport } from 'mediasoup-client/types';
import { Peer, WebSocketTransport } from 'protoo-client';
import { Observable, Subject } from 'rxjs';
import { ApiService } from 'src/app/services/core/api.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})

export class MediasoupService {

  public producerVideoStream: MediaStream | undefined;
  public producerAudioStream: MediaStream | undefined;

  public consumersVideo: Map<string, Consumer> = new Map();
  public consumersAudio: Map<string, Consumer> = new Map();

  public consumersVideoStream: Map<string, MediaStream> = new Map();
  public consumersAudioStream: Map<string, MediaStream> = new Map();
  protooTransport?: WebSocketTransport;
  peer?: Peer;
  peerId: string = Math.floor(Math.random() * 1000000).toString();
  status: EventEmitter<any> = new EventEmitter();
  detections$: Subject<{ peerId: string; boxes: any[] }> = new Subject();
  room: any;
  producerVideoTrack: MediaStreamTrack | undefined;
  producerTracks: Map<string, MediaStreamTrack> = new Map();
  connecting = false;
  peers: Map<string, Peer> = new Map();//peerIds, Peer
  devices: Map<string, Device> = new Map();
  consumerTransports: Map<string, Transport> = new Map();
  producerTransports: Map<string, Transport> = new Map();
  producers: Map<string, any> = new Map(); // key: `${peerId}-${kind}`
  consumerOwners: Map<string, string> = new Map(); // consumerId -> owning (local) peerId
  // Broadcasters currently being displayed; only these get their consumer resumed.
  private watchedBroadcasters: Set<string> = new Set();
  // Connect args per peer so a dead transport can be rebuilt identically.
  private reconnectInfo: Map<string, { room: string; mode: 'dashboard' | 'stream'; acceptAudio: boolean; isProducer: boolean }> = new Map();
  private reconnecting: Set<string> = new Set();
  // Peers closed on purpose (cleanupPeer) so their 'close' doesn't trigger recovery.
  private closedByApp: Set<string> = new Set();

  constructor(
    public http: HttpClient,
    private apiService: ApiService,
    private projectService: ProjectService) { }

  getRoom() {
    return this.room;
  }

  get mediaUrl(): string {
    const target = this.projectService.currentProjectValue?.services?.mediaServer;

    if (target === 'dev' && environment.MEDIA_URL_DEV)
      return environment.MEDIA_URL_DEV;

    return environment.MEDIA_URL;
  }

    async connect(peerId: string, room: string, isProducer = false): Promise<void> {
    // peerId = "mcazelle";
    this.room = room;
    this.connecting = true;
    this.reconnectInfo.set(peerId, { room, mode: 'dashboard', acceptAudio: false, isProducer });
    this.closedByApp.delete(peerId);
    const wsUrl = `${this.mediaUrl}/?roomId=${room}&peerId=${peerId}`;
    let peer = new Peer(new WebSocketTransport(wsUrl));
    this.peers.set(peerId, peer);
    peer.on('open', async () => {

      await this.initializeDevice(peerId, false, 'Dashboard');

    });

    peer.on('failed', () => {
      console.error('Protoo connection failed.');
      this.recoverPeer(peerId, 'protoo failed');
    });

    peer.on('disconnected', () => {
      console.warn('Protoo connection disconnected.');
    });

    peer.on('close', () => {
      console.error('Protoo connection closed.');
      this.recoverPeer(peerId, 'protoo closed');
    });

    peer.on('request', async (request: any, accept: any, reject: any) => {

      // Every protoo request must be answered or the server's _createConsumer
      // times out. Dashboard widgets consume video only, so ack other kinds
      // without building a consumer.
      if (request.method !== 'newConsumer' || request.data.kind !== 'video') {
        accept();
        return;
      }

      const {
        producerId,
        id,
        kind,
        rtpParameters,
        type,
        appData,
        producerPaused,
      } = request.data;

      const consumerTransport = this.consumerTransports.get(peerId);
      if (!consumerTransport) {
        console.error('No consumer transport found for peer:', peerId);
        reject(new Error('No consumer transport'));
        return;
      }

      const consumer = await consumerTransport.consume({
        id,
        producerId,
        kind,
        rtpParameters,
        appData: { ...appData, peerId: request.data.peerId },
      });
      consumer.on('transportclose', () => {
        console.error('Consumer transport closed');
      });

      consumer.on("@close", () => {
        console.error('Producer closed');
        consumer.close();
      });

      const stream = new MediaStream([consumer.track]);
      this.consumersVideoStream.set(request.data.peerId, stream);

      const { track } = consumer;
      this.producerTracks.set(request.data.peerId, track);
      this.consumersVideo.set(consumer.id, consumer);
      this.consumerOwners.set(consumer.id, peerId);

      accept();
      this.status.emit({ peerId, producerId, broadcasterId: request.data.peerId });

      // Resume only displayed broadcasters; pause the rest (delayed to beat the
      // server's auto-resume on accept) so idle cameras can stop.
      const broadcasterId = request.data.peerId;
      if (this.watchedBroadcasters.has(broadcasterId)) {
        this.peers.get(peerId)?.request('resumeConsumer', { consumerId: consumer.id }).catch(() => {});
      } else {
        setTimeout(() => {
          if (!this.watchedBroadcasters.has(broadcasterId)) this.pauseVideoConsumer(broadcasterId);
        }, 600);
      }
    });

    peer.on('notification', (notification: any) => {
      if (notification.method === 'consumerClosed')
        this.removeConsumer(notification.data.consumerId);
    });
  }

  async connectStream(peerId: string, room: string, acceptAudio: boolean = false, isProducer: boolean = false): Promise<void> {
    // peerId = "mcazelle";
    this.room = room;
    this.connecting = true;
    this.reconnectInfo.set(peerId, { room, mode: 'stream', acceptAudio, isProducer });
    this.closedByApp.delete(peerId);
    const wsUrl = `${this.mediaUrl}/?roomId=${room}&peerId=${peerId}`;
    let peer = new Peer(new WebSocketTransport(wsUrl, {
      clientConfig: {
        keepalive: true,
        keepaliveInterval: 20000,
        keepaliveGracePeriod: 10000,
      } as any
    }));
    this.peers.set(peerId, peer);

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      let opened = false;
      const done = (fn: () => void) => { if (!settled) { settled = true; fn(); } };

      peer.on('open', async () => {
        // protoo re-emits 'open' every time its own retry re-opens the WebSocket.
        // The server treats that as a fresh join and hands out new transports and
        // consumers, so everything built on the previous socket is dead -- drop it
        // first, or the video elements keep a frozen track from it forever.
        const rejoin = opened;
        opened = true;
        try {
          if (rejoin) {
            console.warn('[mediasoup] protoo re-opened, rebuilding media for:', peerId);
            this.discardPeerMedia(peerId);
            this.status.emit({ type: 'peer-rejoining', peerId });
          }
          await this.initializeDevice(peerId, isProducer, 'HSE');
          if (rejoin) this.status.emit({ type: 'peer-rejoined', peerId, producing: isProducer });
          else done(resolve);
        } catch (err) {
          if (rejoin) console.error('[mediasoup] rejoin failed for', peerId, err);
          else done(() => reject(err));
        }
      });

      peer.on('failed', () => {
        console.error('Protoo connection failed.');
        done(() => reject(new Error('Protoo connection failed')));
      });

      peer.on('close', () => {
        console.error('Protoo connection closed.');
        done(() => reject(new Error('Protoo connection closed')));
      });
    });

    peer.on('disconnected', () => {
      // While recoverPeer owns this peer's reconnection (or we closed it ourselves),
      // the component must NOT also reconnect — two connectStream() calls for the same
      // peerId evict each other on the server and loop endlessly. recoverPeer handles it.
      if (this.isSelfRecovering(peerId)) {
        console.warn('[mediasoup] peer disconnected during self-recovery, suppressing peer-disconnected for:', peerId);
        return;
      }
      console.warn('Protoo connection disconnected for:', peerId);
      this.status.emit({ type: 'peer-disconnected', peerId });
    });

    peer.on('close', () => {
      // Same reasoning as 'disconnected': a close during our own recovery/cleanup is
      // already being handled — re-emitting spawns a competing reconnect that loops.
      if (this.isSelfRecovering(peerId)) {
        console.warn('[mediasoup] peer closed during self-recovery, suppressing peer-closed for:', peerId);
        return;
      }
      console.warn('[mediasoup] post-connection peer closed, emitting peer-closed for:', peerId);
      this.status.emit({ type: 'peer-closed', peerId });
    });

    peer.on('request', async (request: any, accept: any, reject: any) => {
      console.log('[mediasoup] request:', request.method, request.data?.kind ?? '');

      if (request.method == 'newConsumer' && (request.data.kind == "video" || ( request.data.kind == "audio" && acceptAudio))) {
        console.log('[mediasoup] newConsumer for broadcaster:', request.data.peerId, 'kind:', request.data.kind);

        // Camera broadcasters are only shown when added as the visit's stream: take
        // their video paused (resumed by resumeVideoConsumer) and skip their audio.
        const isBroadcaster = !!request.data.appData?.broadcaster;
        if (isBroadcaster && request.data.kind === 'audio') {
          accept();
          return;
        }

        // Don't consume our own produced stream (broadcaster == this peer).
        // if (request.data.peerId === peerId) {
        //   console.log('[mediasoup] skipping self-consume (producer == consumer) for', peerId);
        //   accept();
        //   return;
        // }

        const {
          producerId,
          id,
          kind,
          rtpParameters,
          type,
          appData,
          producerPaused,
        } = request.data;

        const consumerTransport = this.consumerTransports.get(peerId);
        if (!consumerTransport) {
          console.error('No consumer transport found for peer:', peerId);
          return;
        }

        const consumer = await consumerTransport.consume({
          id,
          producerId,
          kind,
          rtpParameters,
          // streamId: `${request.data.peerId}-${appData.share ? 'share' : 'mic-webcam'}`,
          appData: { ...appData, peerId: request.data.peerId }, // Trick.
        });
        consumer.on('transportclose', () => {
          console.error('Consumer transport closed');
        });

        consumer.on("@close", () => {
          console.error('Producer closed for:', request.data.peerId);
          consumer.close();
          this.status.emit({ type: 'producer-ended', broadcasterId: request.data.peerId, peerId });
        });

        const { track } = consumer;
        console.log('kind :>> ', kind);

        if (kind === "audio") {
          this.consumersAudio.set(request.data.peerId, consumer);
        } else {
          this.consumersVideo.set(request.data.peerId, consumer);
        }
        this.consumerOwners.set(consumer.id, peerId);

        // IMPORTANT
        if (!isBroadcaster || this.watchedBroadcasters.has(request.data.peerId)) {
          await this.peers.get(peerId)?.request('resumeConsumer', {
            consumerId: consumer.id
          });
        } else {
          const broadcasterId = request.data.peerId;
          setTimeout(() => {
            if (!this.watchedBroadcasters.has(broadcasterId)) this.pauseVideoConsumer(broadcasterId);
          }, 600);
        }

        const existingStream = this.consumersVideoStream.get(request.data.peerId);
        let stream: MediaStream;

        if (existingStream && existingStream.getTracks().length <= 1 && acceptAudio) {
          // Reuse existing stream if it has no tracks or only one (maybe just audio)
          stream = existingStream;
        } else {
          // Otherwise create a fresh MediaStream
          stream = new MediaStream();
        }

        stream.addTrack(track);
        this.consumersVideoStream.set(request.data.peerId, stream);
        consumer.on("transportclose", () => {
          consumer.close();
          console.log("DELETING CONSUMER STREAM");
          this.consumersVideo.delete(request.data.peerId);
          this.consumersVideoStream.delete(request.data.peerId);
        })

        accept();
        if (kind == "video")
          this.status.emit({ peerId, producerId, broadcasterId: request.data.peerId });
      }

      if (request.method === 'newDataConsumer') {
        console.log('[mediasoup] newDataConsumer from:', request.data.peerId, 'label:', request.data.label);
        const { id, dataProducerId, sctpStreamParameters, label, protocol, appData } = request.data;
        const consumerTransport = this.consumerTransports.get(peerId);
        if (!consumerTransport) {
          console.error('[mediasoup] newDataConsumer: no consumerTransport for', peerId);
          accept(); return;
        }
        // Close any previous data consumer from this peer before creating a new one
        const dcKey = `${peerId}-${request.data.peerId}-${label}`;
        try { (this as any)._dataConsumers?.get(dcKey)?.close(); } catch {}
        try {
          const dataConsumer = await consumerTransport.consumeData({
            id, dataProducerId, sctpStreamParameters, label, protocol, appData,
          });
          if (!(this as any)._dataConsumers) (this as any)._dataConsumers = new Map();
          (this as any)._dataConsumers.set(dcKey, dataConsumer);
          console.log('[mediasoup] dataConsumer created, readyState:', dataConsumer.readyState);
          dataConsumer.on('open', () => console.log('[mediasoup] dataConsumer open'));
          dataConsumer.on('error', (e: any) => console.error('[mediasoup] dataConsumer error:', e));
          dataConsumer.on('message', (message: any) => {
            try {
              console.log('----> message :>>', message);
              const text = typeof message === 'string' ? message : new TextDecoder().decode(message);
              const data = JSON.parse(text);
              this.detections$.next({ peerId: request.data.peerId, ...data });
            } catch {}
          });
        } catch (err) {
          console.error('[mediasoup] consumeData error:', err);
        }
        accept();
      }
    });

    peer.on('notification', (notification: any) => {
      if (notification.method === 'consumerClosed')
        this.removeConsumer(notification.data.consumerId);
    });
  }



  /** Peer ids whose video track this client is currently consuming (the broadcasters). */
  getConsumedVideoBroadcasters(): string[] {
    return [...this.consumersVideo.keys()];
  }

  /** Peer ids for which this client is currently producing a video track. */
  getProducingVideoPeers(): string[] {
    return [...this.producers.keys()]
      .filter((key) => key.endsWith('-video'))
      .map((key) => key.slice(0, -'-video'.length));
  }

  /** Inbound RTP counters for a broadcaster's video consumer. A frozen picture
   * leaves the MediaStreamTrack 'live', so the element and the track both look
   * healthy -- these counters are the only honest liveness signal. Returns null
   * when there is no consumer to measure. */
  async getInboundVideoStats(broadcasterId: string): Promise<{ progress: number; metric: string } | null> {
    const consumer = this.consumersVideo.get(broadcasterId);
    if (!consumer || consumer.closed) return null;
    try {
      const report = await consumer.getStats();
      let stats: { progress: number; metric: string } | null = null;
      report.forEach((entry: any) => {
        // Older WebKit reports mediaType rather than kind.
        if (entry.type !== 'inbound-rtp' || (entry.kind ?? entry.mediaType) !== 'video') return;
        // framesDecoded is the honest signal, but Safari does not expose it on
        // every version. Treating a missing counter as zero would read as a
        // permanent stall, so fall back to bytesReceived, which still moves
        // whenever RTP is arriving, and report nothing when neither exists.
        if (typeof entry.framesDecoded === 'number') {
          stats = { progress: entry.framesDecoded, metric: 'framesDecoded' };
        } else if (typeof entry.bytesReceived === 'number') {
          stats = { progress: entry.bytesReceived, metric: 'bytesReceived' };
        }
      });
      return stats;
    } catch (err) {
      console.warn('[mediasoup] getInboundVideoStats failed for', broadcasterId, err);
      return null;
    }
  }

  getConsumerStream(broadcasterId: string): MediaStream|undefined {
    let stream = undefined;
    try {
      stream = this.consumersVideoStream.get(broadcasterId);
    } catch (err) {
      console.log('err :>> ', err);
    }
    return stream;
  }

  async initiateProducer(peerId: string) {
    await this.createProducerTransport(peerId);
  }

  private async createProducerTransport(peerId: string) {
    const peer = this.peers.get(peerId);
    const device = this.devices.get(peerId);

    const transportInfo = await peer?.request("createWebRtcTransport", {
      producing: true,
      consuming: false,
      forceTcp: true
    });
    const {
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters,
      sctpParameters,
    } = transportInfo;

    // let consumerTransport = device?.createRecvTransport({
    //   id,
    //   iceParameters,
    //   iceCandidates,
    //   dtlsParameters: {
    //     ...dtlsParameters,
    //     role: 'auto',
    //   },
    //   sctpParameters,
    //   iceServers: []
    // });

    const transport = device?.createSendTransport({
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters: {
        ...dtlsParameters,
        role: 'auto',
      },
      sctpParameters,
      iceServers: []
    }); // transportInfo

    if (!transport) return;

    transport.on('connect', async (opts, callback, errback) => {
      try {
        await peer?.request('connectWebRtcTransport', {
          transportId: transport.id,
          dtlsParameters: opts.dtlsParameters // dtlsParameters
        });
        callback();
      } catch (err) {
        errback(err as Error);
      }
    });

    transport.on('produce', async ({ kind, rtpParameters, appData }, callback) => {
      const { id } = await peer?.request('produce', {
        transportId: transport.id,
        kind,
        rtpParameters,
        appData: { ...appData }
      });
      callback({ id });
    });

    this.producerTransports.set(peerId, transport);
    this.watchTransport(peerId, transport, 'producer');
  }

  /**
   * Initialize the Mediasoup device with the server's RTP capabilities
   */
  private async initializeDevice(peerId: string, isProducer: boolean = false, name: string = 'Dashboard') {
    const peer = this.peers.get(peerId);
    if (!peer) {
      throw new Error('Peer not found');
    }

    const rtpCapabilities = await peer.request('getRouterRtpCapabilities');
    let device = new Device();
    this.devices.set(peerId, device);
    await device.load({ routerRtpCapabilities: rtpCapabilities });
    await this.createConsumerTransport(peerId);

    if (isProducer)
      await this.createProducerTransport(peerId);

    const { peers } = await peer.request('join', {
      displayName: "Unifield",
      device: {
        flag: "Unifield",
        name,
        version: "000",
      },
      rtpCapabilities: device.rtpCapabilities,
      sctpCapabilities: device.sctpCapabilities,
    });

  }

  /**
   * Create a consumer transport for receiving media
   */
  private async createConsumerTransport(peerId: string) {
    const peer = this.peers.get(peerId);
    const device = this.devices.get(peerId);

    if (!peer || !device) {
      throw new Error('Peer or device not found');
    }

    const transportInfo = await peer.request(
      'createWebRtcTransport',
      {
        forceTcp: true,
        producing: false,
        consuming: true,
        sctpCapabilities: device.sctpCapabilities
      }
    );

    const {
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters,
      sctpParameters,
    } = transportInfo;

    let consumerTransport = device.createRecvTransport({
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters: {
        ...dtlsParameters,
        role: 'auto',
      },
      sctpParameters,
      iceServers: []
    });

    this.consumerTransports.set(peerId, consumerTransport);
    this.watchTransport(peerId, consumerTransport, 'consumer');

    consumerTransport.on('connect', async (data: { dtlsParameters: any; }, callback: () => void, errback: (error: Error) => void) => {
      try {

        await peer.request('connectWebRtcTransport', {
          transportId: consumerTransport.id,
          dtlsParameters: data.dtlsParameters,
        });

        this.status.emit("ready");
        this.status.emit({ type: "consumer-ready", peerId });
        callback();

      } catch (error) {
        console.error('Failed to connect transport:', error);
        errback(error as Error);
      }
    });

  }

  /**
   * Consume video from the server
   * @param producerId The ID of the producer to consume
   */
  consumeVideoTrack(producerId: string): MediaStreamTrack {
    const track = this.producerTracks.get(producerId);

    if (!track) {
      throw new Error(`No video track found for producerId: ${producerId}`);
    }

    return track;
  }

  async produceTrack(producerId: string, track: MediaStreamTrack) {
    let producer = this.producerTransports.get(producerId);
    if (!producer) {
      throw new Error('Producer transport not ready');
    }

    if (!this.producerTransports.has(producerId))
      this.producerTransports.set(producerId, producer);

    let prod = await producer.produce({ track });
    this.producers.set(`${producerId}-${track.kind}`, prod);
    this.status.emit({
      type: 'producer-ready',
      producerId,
      kind: track.kind
    });
    track.onended = () => {
      // Close only THIS producer, not the whole transport (`producer`) — tearing down
      // the transport kills every producer on it and makes the stream unrecoverable.
      try { prod.close(); } catch {}
      this.producers.delete(`${producerId}-${track.kind}`);
      this.status.emit({
        type: 'producer-ended',
        producerId
      })
    }
    return prod;
  }


  /**
   * Consume video from the server
   * @param producerId The ID of the producer to consume
   */
  consumeVideo(producerId: string): MediaStream {
    if (!this.producerVideoStream) {
      throw new Error('No video stream available');
    }
    return this.producerVideoStream;
  }

  isPeerReady(peerId: string): boolean {
    const peer = this.peers.get(peerId);
    // Not ready until initializeDevice has completed (consumer transport created)
    return !!peer && !peer.closed && this.consumerTransports.has(peerId);
  }

  /** True while the protoo WebSocket is up. Goes false on 'disconnected' and back
   * to true once protoo's own retry re-opens it. */
  isPeerConnected(peerId: string): boolean {
    const peer = this.peers.get(peerId);
    return !!peer && !peer.closed && peer.connected;
  }

  /** Drop the protoo WebSocket for a peer — use in devtools console to test peer-disconnected handling. */
  simulateDisconnect(peerId: string) {
    const peer = this.peers.get(peerId);
    if (!peer) { console.warn('[mediasoup] simulateDisconnect: no peer for', peerId); return; }
    (peer as any)._transport._ws?.close();
  }

  async replaceVideoTrack(peerId: string, newTrack: MediaStreamTrack): Promise<void> {
    const producer = this.producers.get(`${peerId}-video`);
    if (!producer) throw new Error(`No video producer for peer: ${peerId}`);
    await producer.replaceTrack({ track: newTrack });
  }

  /**
   * Watch a mediasoup transport's ICE/DTLS state. When it reaches 'failed' (e.g.
   * the server closed it after ICE consent expired during a long pause, laptop
   * sleep, VPN/network change), rebuild the whole peer instead of reusing the
   * dead transport -- previously only a page reload recovered from this.
   */
  private watchTransport(peerId: string, transport: Transport, label: 'consumer' | 'producer') {
    let graceTimer: any = null;
    // A protoo auto-reconnect re-runs initializeDevice, and cleanupPeer/recoverPeer
    // replace the transports too, so a peer can outlive several of them. An abandoned
    // transport still emits 'failed' when it dies -- acting on it would tear down the
    // peer that replaced it, which is what turned a single blip into a rebuild loop.
    const isCurrent = () =>
      this.consumerTransports.get(peerId) === transport ||
      this.producerTransports.get(peerId) === transport;

    transport.on('connectionstatechange', (state: string) => {
      const stale = !isCurrent();
      console.warn(`[mediasoup] ${label} transport state '${state}' for peer:`, peerId, stale ? '(stale, ignored)' : '');
      if (stale) {
        if (graceTimer) { clearTimeout(graceTimer); graceTimer = null; }
        return;
      }
      if (state === 'failed') {
        if (graceTimer) { clearTimeout(graceTimer); graceTimer = null; }
        this.recoverPeer(peerId, `${label} transport failed`);
      } else if (state === 'disconnected') {
        // Give ICE a short window to self-recover (server no longer closes on
        // 'disconnected'), then rebuild without waiting for the slow 'failed'.
        if (!graceTimer) {
          graceTimer = setTimeout(() => {
            graceTimer = null;
            if (isCurrent() && transport.connectionState !== 'connected') {
              this.recoverPeer(peerId, `${label} transport stuck disconnected`);
            }
          }, 3000);
        }
      } else if (state === 'connected' && graceTimer) {
        clearTimeout(graceTimer);
        graceTimer = null;
      }
    });
  }

  /** Tear down a peer whose media transport died and reconnect it with the same
   * arguments. Rejoining makes the server recreate the consumers; widgets get the
   * usual status/newConsumer events, and watchedBroadcasters is preserved so
   * displayed cameras auto-resume. */
  /** True while mediasoup is already rebuilding this peer (recoverPeer in flight) or has
   * deliberately closed it (cleanupPeer). In either case the component must not launch a
   * second, competing reconnect. */
  private isSelfRecovering(peerId: string): boolean {
    return this.reconnecting.has(peerId) || this.closedByApp.has(peerId);
  }

  private async recoverPeer(peerId: string, reason: string): Promise<void> {
    if (this.reconnecting.has(peerId)) return;
    if (this.closedByApp.has(peerId)) { this.closedByApp.delete(peerId); return; }
    const info = this.reconnectInfo.get(peerId);
    if (!info) {
      console.warn('[mediasoup] recoverPeer: no reconnect info for', peerId, '- skipping');
      return;
    }
    this.reconnecting.add(peerId);
    console.warn('[mediasoup] recovering peer', peerId, '-', reason);
    this.status.emit({ type: 'reconnecting', peerId, reason });

    this.dropPeerConsumers(peerId);
    this.cleanupPeer(peerId);

    const maxAttempts = 6;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        if (info.mode === 'stream') {
          await this.connectStream(peerId, info.room, info.acceptAudio, info.isProducer);
        } else {
          await this.connect(peerId, info.room, info.isProducer);
        }
        this.reconnecting.delete(peerId);
        this.closedByApp.delete(peerId);
        console.warn('[mediasoup] peer recovered:', peerId);
        this.status.emit({ type: 'reconnected', peerId, producing: info.isProducer });
        return;
      } catch (err) {
        console.warn(`[mediasoup] reconnect attempt ${attempt}/${maxAttempts} failed for`, peerId, err);
        await new Promise((r) => setTimeout(r, Math.min(2000 * attempt, 10000)));
      }
    }
    this.reconnecting.delete(peerId);
    this.status.emit({ type: 'reconnect-failed', peerId });
  }

  /** Close and forget every consumer owned by a (local) peer, so a rebuilt peer
   * starts clean and widgets don't hold tracks from the dead transport. */
  private dropPeerConsumers(peerId: string) {
    for (const map of [this.consumersVideo, this.consumersAudio]) {
      for (const [key, consumer] of [...map]) {
        if (this.consumerOwners.get(consumer.id) !== peerId) continue;
        const broadcasterId = (consumer.appData as any)?.peerId ?? key;
        try { consumer.close(); } catch {}
        map.delete(key);
        this.consumerOwners.delete(consumer.id);
        this.consumersVideoStream.delete(broadcasterId);
        this.producerTracks.delete(broadcasterId);
      }
    }
  }

  /** Drop everything a peer built on a previous protoo socket -- consumers,
   * transports, producers, device -- while keeping the peer itself alive. */
  private discardPeerMedia(peerId: string) {
    this.dropPeerConsumers(peerId);
    try { this.consumerTransports.get(peerId)?.close(); } catch {}
    try { this.producerTransports.get(peerId)?.close(); } catch {}
    this.consumerTransports.delete(peerId);
    this.producerTransports.delete(peerId);
    this.producers.delete(`${peerId}-video`);
    this.producers.delete(`${peerId}-audio`);
    this.devices.delete(peerId);
    this.consumersVideoStream.delete(peerId);
  }

  cleanupPeer(peerId: string) {
    console.warn('[mediasoup] cleanupPeer:', peerId);
    this.closedByApp.add(peerId);
    try { this.peers.get(peerId)?.close(); } catch {}
    try { this.consumerTransports.get(peerId)?.close(); } catch {}
    try { this.producerTransports.get(peerId)?.close(); } catch {}
    this.consumerTransports.delete(peerId);
    this.producerTransports.delete(peerId);
    this.producers.delete(`${peerId}-video`);
    this.producers.delete(`${peerId}-audio`);
    this.devices.delete(peerId);
    this.peers.delete(peerId);
    this.consumersVideoStream.delete(peerId);
  }

  /** Drop a consumer the server closed (producer stopped/recreated): clear its
   * maps and notify listeners so they stop consuming the (now absent) stream. */
  private removeConsumer(consumerId: string): void {
    let broadcasterId: string | undefined;
    let owner: string | undefined;

    for (const map of [this.consumersVideo, this.consumersAudio]) {
      for (const [key, consumer] of map) {
        if (consumer.id === consumerId) {
          broadcasterId = (consumer.appData as any)?.peerId;
          owner = this.consumerOwners.get(consumer.id);
          try { consumer.close(); } catch {}
          map.delete(key);
        }
      }
    }
    this.consumerOwners.delete(consumerId);

    if (broadcasterId) {
      this.consumersVideoStream.delete(broadcasterId);
      this.producerTracks.delete(broadcasterId);
      this.status.emit({ type: 'producer-ended', broadcasterId, peerId: owner });
    }
  }

  /** Find the video consumer for a broadcaster, whether stored by broadcaster id or by consumer id. */
  private findVideoConsumer(broadcasterId: string): Consumer | undefined {
    const direct = this.consumersVideo.get(broadcasterId);
    if (direct) return direct;
    for (const consumer of this.consumersVideo.values()) {
      if ((consumer.appData as any)?.peerId === broadcasterId) return consumer;
    }
    return undefined;
  }

  /** Pause consuming a broadcaster's video (server stops sending RTP to this consumer). Reversible. */
  async pauseVideoConsumer(broadcasterId: string): Promise<void> {
    this.watchedBroadcasters.delete(broadcasterId);
    const consumer = this.findVideoConsumer(broadcasterId);
    if (!consumer) return;
    try { consumer.pause(); } catch {}
    const owner = this.consumerOwners.get(consumer.id);
    try {
      await this.peers.get(owner!)?.request('pauseConsumer', { consumerId: consumer.id });
    } catch (err) {
      console.warn('[mediasoup] pauseConsumer request failed', err);
    }
  }

  /** Resume consuming a broadcaster's video previously paused with pauseVideoConsumer. */
  async resumeVideoConsumer(broadcasterId: string): Promise<void> {
    this.watchedBroadcasters.add(broadcasterId); // before any consumer exists
    const consumer = this.findVideoConsumer(broadcasterId);
    if (!consumer) return;
    const owner = this.consumerOwners.get(consumer.id);

    // If the transport died during the pause (throttled tab, sleep, network
    // change), resuming on it would show a freeze first - recover right away.
    const transport = owner ? this.consumerTransports.get(owner) : undefined;
    if (owner && transport && transport.connectionState !== 'connected'
        && transport.connectionState !== 'connecting' && transport.connectionState !== 'new') {
      console.warn('[mediasoup] resume on dead transport (%s) - recovering first', transport.connectionState);
      this.recoverPeer(owner, 'resume on dead transport');
      return; // widgets re-attach via reconnected/newConsumer events; watchedBroadcasters keeps it resumed
    }
    try {
      await this.peers.get(owner!)?.request('resumeConsumer', { consumerId: consumer.id });
    } catch (err) {
      console.warn('[mediasoup] resumeConsumer request failed', err);
    }
    try { consumer.resume(); } catch {}
  }

  async stopProducing(peerId: string): Promise<void> {
    // Close each producer client-side and tell the server to close it too, then
    // tear down the producer transport (which also stops the server-side connection).
    for (const kind of ['video', 'audio']) {
      const key = `${peerId}-${kind}`;
      const producer = this.producers.get(key);
      if (!producer) continue;
      const producerId = producer.id;
      try { producer.close(); } catch {}
      try {
        await this.peers.get(peerId)?.request('closeProducer', { producerId });
      } catch (err) {
        // Server may not expose closeProducer; the transport close below still ends it.
        console.warn('[mediasoup] closeProducer request failed for', key, err);
      }
      this.producers.delete(key);
    }
    try { this.producerTransports.get(peerId)?.close(); } catch {}
    this.producerTransports.delete(peerId);
  }

  startDetect(room: string, peerId: string): Observable<any> {
    return this.apiService.postCollectionData('mediasoup', 'detect', { room, peerId });
  }

  stopDetect(room: string, peerId: string): Observable<any> {
    return this.apiService.postCollectionData('mediasoup', 'stop-detect', { room, peerId });
  }
}
